import { Grid, InputAdornment, TextField } from "@mui/material"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { UserModuleProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as User } from '../../assets/SideBarIcons/users.svg';
import { useEffect, useMemo, useState } from "react";
import UserTabs from "./UserTabs";
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GetUserByIdResponse } from "@subModule/src/services/user/interface";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

interface FormData { [key: string]: any; }

export default function UpdatePassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const userId = params?.id;
    const { enqueueSnackbar } = useSnackbar();
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<FormData>({ defaultValues: {} });
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(true);
    const [userName, setUserName] = useState('');

    useEffect(() => {
        if (params?.id) getByID();
    }, [params]);
    const getByID = () => {
        UserModuleProfileService.getUserByID(+(params as any)?.id).then((res: GetUserByIdResponse) => {
            if (res.success) {
                setValue('email', res?.data.email);
                const name = String(res?.data?.name || "").trim();
                if (name) setUserName(name);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        delete data.showPassword; delete data.showConfirmPassword; delete data.confirmNewPassword;
        UserModuleProfileService.updatePassword(data).then((response: any) => {
            if (response.success) {
                const email = data.email;
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('password') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                reset();
                setValue('email', email)
            } else {
                enqueueSnackbar(<AlertMessages text={response?.message} />, { variant: 'error' });
            }
            setIsBtnEnabled(true);
        }).catch((err: any) => {
            console.error("===", err);
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
        });
    };

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: "general" },
            { labelKey: "users", href: "/users" },
            {
                labelKey: "users",
                ...(userName ? { label: userName } : {}),
                href: userId ? `/user/${userId}` : undefined,
            },
            { labelKey: "updatePassword" },
        ],
        [userId, userName],
    );

    return (<>
        <ComponentHeaderTwo
            showBtn={true}
            showSaveBtn={true}
            onClick={() => navigate('/users')}
            onSubmit={handleSubmit(onSubmit)}
            heading={t('editUser')}
            icon={<User height={28} width={28} />}
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={false}
            breadcrumbItems={breadcrumbItems}
        />
        <div className={`${params?.id ? 'flex' : ''}`}>
            {params?.id ? <UserTabs params={params} selectedIndex={1} /> : null}
            <SimpleModal
                visable={true}
                title={t('editUser')}
                isButtonVisable={true}
                isVisableBtn={IsBtnEnabled}
                btnclosetext="Back"
                buttonText={params?.id ? "update" : "submit"}
                attBtnNotshow={false}
                notVisableBackbtn={true}
                showPortList={handleSubmit(onSubmit)}
                formSubmit={handleSubmit(onSubmit)}
                content={
                    <Grid container component="form" id="form" className="items-center mb-2" spacing={3} onSubmit={handleSubmit(onSubmit)}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                type={watch('showPassword') ? 'text' : 'password'}
                                {...register('newPassword', {
                                    required: t('This field is required.'),
                                    validate: { passwordLength: (value) => value.length >= 8 || t('minPasswordLength'), },
                                })}
                                label={t('password') + ' *'}
                                placeholder={t('password')}
                                title={t('password')}
                                fullWidth
                                error={!!errors.newPassword}
                                helperText={errors.newPassword?.message as string}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (watch('newPassword') ? <InputAdornment position="end" className="cursor-pointer">
                                        {watch('showPassword') ? (
                                            <VisibilityIcon style={{ color: themeColors?.primary }} onClick={() => setValue('showPassword', false)} />
                                        ) : (
                                            <VisibilityOffIcon style={{ color: themeColors?.primary }} onClick={() => setValue('showPassword', true)} />
                                        )}
                                    </InputAdornment> : null),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                type={watch('showConfirmPassword') ? 'text' : 'password'}
                                {...register("confirmNewPassword", {
                                    required: t('This field is required.'),
                                    validate: { passwordLength: (value) => (value === watch('newPassword')) || t('confirmPasswordNewPassword'), },
                                },)}
                                label={t("confirmNewPassword") + ' *'}
                                placeholder={t("confirmNewPassword")}
                                title={t("confirmNewPassword")}
                                fullWidth
                                error={!!errors.confirmNewPassword}
                                helperText={errors.confirmNewPassword?.message as string}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (watch('confirmNewPassword') ? <InputAdornment position="end" className="cursor-pointer">
                                        {watch('showConfirmPassword') ? (
                                            <VisibilityIcon style={{ color: themeColors?.primary }} onClick={() => setValue('showConfirmPassword', false)} />
                                        ) : (
                                            <VisibilityOffIcon style={{ color: themeColors?.primary }} onClick={() => setValue('showConfirmPassword', true)} />
                                        )}
                                    </InputAdornment> : null),
                                }}
                            />
                        </Grid>
                    </Grid>
                }
            />
        </div>
    </>)
}
